import type { Language, QuoteStatus } from "../domain/quote";

/** Texts shown to clients in the quote document, the portal and the emails. */
export interface Labels {
  quote: string;
  folio: string;
  date: string;
  validUntil: string;
  preparedFor: string;
  taxId: string;
  concept: string;
  quantity: string;
  unitPrice: string;
  amount: string;
  optional: string;
  notIncluded: string;
  subtotal: string;
  taxIva: string;
  taxExempt: string;
  total: string;
  terms: string;
  paymentShopify: string;
  paymentTransfer: string;
  expired: string;
  accepted: string;
  acceptedBy: string;
  downloadPdf: string;
  myQuotes: string;
  status: Record<QuoteStatus, string>;
  page: string;
  acceptTitle: string;
  acceptIntro: string;
  chooseOptions: string;
  yourName: string;
  yourPosition: string;
  confirmAccept: string;
  acceptButton: string;
  accepting: string;
  acceptedThanks: string;
  payNow: string;
  invoiceSent: string;
  transferNext: string;
}

const ES: Labels = {
  quote: "Cotización",
  folio: "Folio",
  date: "Fecha",
  validUntil: "Vigente hasta",
  preparedFor: "Preparada para",
  taxId: "RFC / ID fiscal",
  concept: "Concepto",
  quantity: "Cant.",
  unitPrice: "Precio unitario",
  amount: "Importe",
  optional: "Opcional",
  notIncluded: "no incluida en el total",
  subtotal: "Subtotal",
  taxIva: "IVA 16 %",
  taxExempt: "Impuestos (exento)",
  total: "Total",
  terms: "Condiciones",
  paymentShopify: "Al aceptar recibirás un enlace de pago seguro.",
  paymentTransfer: "Pago por transferencia bancaria contra factura.",
  expired: "Esta cotización ya no está vigente.",
  accepted: "Cotización aceptada",
  acceptedBy: "Aceptada por",
  downloadPdf: "Descargar PDF",
  myQuotes: "Mis cotizaciones",
  status: { draft: "Borrador", sent: "Pendiente", viewed: "Pendiente", accepted: "Aceptada", cancelled: "Cancelada" },
  page: "Página",
  acceptTitle: "Aceptar cotización",
  acceptIntro: "Revisa las partidas y, si todo está correcto, acepta la cotización.",
  chooseOptions: "Elige las partidas opcionales que quieres incluir:",
  yourName: "Nombre completo",
  yourPosition: "Puesto",
  confirmAccept: "Confirmo que acepto esta cotización en nombre de mi empresa.",
  acceptButton: "Aceptar cotización",
  accepting: "Enviando…",
  acceptedThanks: "¡Gracias! Recibimos tu aceptación.",
  payNow: "Pagar ahora",
  invoiceSent: "También te enviamos el enlace de pago por correo.",
  transferNext: "Te enviaremos la factura y los datos para el pago.",
};

const EN: Labels = {
  quote: "Quote",
  folio: "Quote no.",
  date: "Date",
  validUntil: "Valid until",
  preparedFor: "Prepared for",
  taxId: "Tax ID",
  concept: "Item",
  quantity: "Qty",
  unitPrice: "Unit price",
  amount: "Amount",
  optional: "Optional",
  notIncluded: "not included in total",
  subtotal: "Subtotal",
  taxIva: "VAT 16 %",
  taxExempt: "Tax (exempt)",
  total: "Total",
  terms: "Terms",
  paymentShopify: "Once accepted you will receive a secure payment link.",
  paymentTransfer: "Payment by bank transfer against invoice.",
  expired: "This quote is no longer valid.",
  accepted: "Quote accepted",
  acceptedBy: "Accepted by",
  downloadPdf: "Download PDF",
  myQuotes: "My quotes",
  status: { draft: "Draft", sent: "Pending", viewed: "Pending", accepted: "Accepted", cancelled: "Cancelled" },
  page: "Page",
  acceptTitle: "Accept quote",
  acceptIntro: "Review the items and, if everything is right, accept the quote.",
  chooseOptions: "Choose the optional items you want to include:",
  yourName: "Full name",
  yourPosition: "Job title",
  confirmAccept: "I confirm I accept this quote on behalf of my company.",
  acceptButton: "Accept quote",
  accepting: "Sending…",
  acceptedThanks: "Thank you! We received your acceptance.",
  payNow: "Pay now",
  invoiceSent: "We also emailed you the payment link.",
  transferNext: "We will send you the invoice and payment details.",
};

export function labelsFor(language: Language): Labels {
  return language === "en" ? EN : ES;
}
